import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, ShieldCheck, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Loader, StatusBadge } from "@/components/shared";

export default function Verification() {
  const [kyc, setKyc] = useState(null);
  const [form, setForm] = useState({ full_name: "", date_of_birth: "", address: "", id_type: "passport", id_number: "" });
  const [doc, setDoc] = useState("");
  const [selfie, setSelfie] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => { const r = await api.get("/kyc"); setKyc(r.data); };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onFile = (setter) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) return toast.error("Only JPG, PNG, WEBP");
    if (file.size > 5 * 1024 * 1024) return toast.error("Max 5MB");
    const reader = new FileReader();
    reader.onload = () => setter(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!doc || !selfie) return toast.error("Please upload both document and selfie");
    setLoading(true);
    try {
      await api.post("/kyc", { ...form, document_url: doc, selfie_url: selfie });
      toast.success("KYC submitted successfully. Pending review.");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  if (kyc === null) return <Loader />;
  const status = kyc.status || "not_submitted";
  const canSubmit = status === "not_submitted" || status === "rejected";

  return (
    <div className="space-y-6" data-testid="verification-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="label-eyebrow text-xs font-mono uppercase tracking-[0.2em] text-cyan-400">KYC Verification</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white mt-1">Identity Verification</h1>
        </div>
        <StatusBadge status={status} />
      </div>

      {status === "pending" && <div className="glass rounded-2xl p-6 text-slate-300">Your verification is under review. We'll notify you once it's processed.</div>}
      {status === "approved" && <div className="glass rounded-2xl p-6 flex items-center gap-3 border-emerald-500/20"><ShieldCheck className="w-6 h-6 text-emerald-400" /><p className="text-slate-200">Your identity is verified.</p></div>}
      {status === "rejected" && kyc.rejection_reason && <div className="glass rounded-2xl p-4 border-red-500/20 text-red-300 text-sm">Rejected: {kyc.rejection_reason}. Please resubmit.</div>}

      {canSubmit && (
        <form onSubmit={submit} className="glass rounded-2xl p-6 space-y-4 max-w-2xl">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-sm text-slate-300 mb-1.5 block">Full Name</label>
              <input data-testid="kyc-fullname" required value={form.full_name} onChange={set("full_name")} className="w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 focus:border-cyan-400 focus:outline-none" /></div>
            <div><label className="text-sm text-slate-300 mb-1.5 block">Date of Birth</label>
              <input data-testid="kyc-dob" type="date" required value={form.date_of_birth} onChange={set("date_of_birth")} className="w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 focus:border-cyan-400 focus:outline-none" /></div>
          </div>
          <div><label className="text-sm text-slate-300 mb-1.5 block">Address</label>
            <input data-testid="kyc-address" required value={form.address} onChange={set("address")} className="w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 focus:border-cyan-400 focus:outline-none" /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="text-sm text-slate-300 mb-1.5 block">ID Type</label>
              <select data-testid="kyc-idtype" value={form.id_type} onChange={set("id_type")} className="w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 focus:border-cyan-400 focus:outline-none">
                <option value="passport" className="bg-slate-900">Passport</option>
                <option value="national_id" className="bg-slate-900">National ID</option>
                <option value="drivers_license" className="bg-slate-900">Driver's License</option>
              </select></div>
            <div><label className="text-sm text-slate-300 mb-1.5 block">ID Number</label>
              <input data-testid="kyc-idnumber" required value={form.id_number} onChange={set("id_number")} className="w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 focus:border-cyan-400 focus:outline-none" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[["Document Upload", doc, setDoc, "kyc-document"], ["Selfie Upload", selfie, setSelfie, "kyc-selfie"]].map(([label, val, setter, tid]) => (
              <div key={tid}>
                <label className="text-sm text-slate-300 mb-1.5 block">{label}</label>
                {val ? (
                  <div className="relative"><img src={val} alt="" className="w-full h-32 object-cover rounded-lg border border-cyan-500/20" />
                    <button type="button" onClick={() => setter("")} className="absolute top-2 right-2 w-6 h-6 rounded-full bg-slate-900/80 flex items-center justify-center"><X className="w-3.5 h-3.5 text-slate-200" /></button></div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-cyan-500/20 rounded-lg cursor-pointer hover:border-cyan-500/40">
                    <Upload className="w-5 h-5 text-cyan-400 mb-1" /><span className="text-xs text-slate-500">Upload</span>
                    <input data-testid={tid} type="file" accept="image/*" className="hidden" onChange={onFile(setter)} />
                  </label>
                )}
              </div>
            ))}
          </div>
          <button type="submit" disabled={loading} data-testid="kyc-submit"
            className="btn-glow w-full py-3 rounded-lg font-semibold text-slate-950 inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Submit Verification
          </button>
        </form>
      )}
    </div>
  );
}
